// Taktisk bilde fra GNÅ til SKADE: bildet SELV, EXIF, sensorens attitude og
// synsfelt — over samme autentiserte HTTPS-vei som posisjon og geotag.
//
// ── HVORFOR DENNE FILA FINNES ───────────────────────────────────────────────
// EIERFUNN 2026-08-17: «Jeg forsøkte å sende et bilde til SKADE fra GNÅ.
// Posisjonen kommer opp — men bildet kommer ikke, heller ikke EXIF-data. Jeg vil
// ikke at det skal se ut som det er sendt en melding når du sender inn et bilde.»
//
// ⚠ DETTE ER EN REVERSERING AV EN BEVISST BESLUTNING, IKKE EN FEILRETTING.
// `gna-posisjon.ts` sier ordrett, om geotaggen: «Merk hva som IKKE flyttes:
// BILDET i en geotag blir liggende som vedlegg i Matrix-rommet, der mennesker
// leser det. Det er punktet og teksten SKADE trenger for situasjonsbildet, og et
// bilde er ikke telemetri. Det er en ærlig, bevisst grense — ikke en glemt del.»
// Eieren har omgjort den. Grunnloven §4 krever at reverseringer av bevisste
// isolasjonsvalg er eksplisitte og dokumenterte — derfor står den her, ikke i en
// commit-melding som ingen leser om seks måneder.
//
// ── HVORFOR IKKE `mxc://` (den eksterne mulighetsstudiens forslag) ──────────
// En ekstern konsulentstudie foreslo at appen laster bildet til Matrix' mediearkiv
// og sender `mxc://`-URI-en i en egendefinert hendelse, som SKADE så henter.
// DET VIRKER IKKE HOS OSS, og det er verdt å skrive ned hvorfor, så forslaget
// ikke kommer tilbake: broens nøkkellager ligger i MINNET (Node har ingen
// IndexedDB), så den får ny device-id ved hver omstart og telefonene har aldri
// delt megolm-nøkler med den. Hubben kan altså ikke dekryptere mediet. Å gjøre
// rommet ukryptert for å komme rundt det ville lagt feltbilder i klartekst i
// Synapse — strengt verre OPSEC enn i dag. Se `gna-posisjon.ts:8–14`, der samme
// vei alt er dokumentert brutt for posisjoner.
//
// ── HVORFOR EGET ENDEPUNKT OG IKKE TELEMETRI-KONVOLUTTEN ────────────────────
// `GnaTelemetriPakke` tar inntil 50 hendelser i én forsendelse (kø tømt etter
// nettbrudd). Et bilde i den unionen ville gjort maks kroppsstørrelse
// 50 × bildegrensa — altså titalls megabyte, fra en uautentisert kroppsparser.
// Bilder får derfor sin egen forsendelse med ETT bilde og egen grense. Det er
// ikke pynt: fanout og ingest deler event-loop med posisjonene, og en telefon som
// tømmer et bildekø skal ikke kunne sulte situasjonsbildet (§6).
import { z } from 'zod';
import { decimalYear, magneticDeclination } from './magdec.js';
/**
 * Største bilde vi tar imot, som base64-tegn (~1,3 MB rådata).
 *
 * Appen SKAL skalere ned før sending. Et fullt 12 MP-bilde er 4–6 MB, og over et
 * degradert samband blokkerer det alt annet fra samme telefon i minutter — mens
 * et 1024 px JPEG på ~150 kB viser operatøren det samme. Grensa er satt slik at
 * en fornuftig nedskalering alltid passer, og en rå kamerafil alltid avvises med
 * en tydelig feil i stedet for å tømme sambandet (§6).
 */
export const BILDE_MAKS_BASE64 = 1_800_000;
/**
 * Hvor bildet kommer fra.
 *
 * ⚠ SIKKERHETSFELT, IKKE METADATA-PYNT. En operatør som laster opp et bilde fra
 * galleriet kan laste opp noe tatt i fjor, et annet sted, av en annen. Uten dette
 * skillet ville et slikt bilde blitt lest som en sanntidsobservasjon og fått
 * samme vekt i situasjonsbildet som et ferskt kamerabilde. `ukjent` finnes fordi
 * eldre appversjoner ikke sender feltet, og fordi «vi vet ikke» skal være mulig å
 * si — ikke stilles om til `live_camera` av en default.
 */
export const BildeOpphavSchema = z.enum(['live_camera', 'gallery_import', 'ukjent']);
/**
 * EXIF-felt vi faktisk kan bruke operativt. Bevisst kort liste: dette er ikke en
 * EXIF-dump, det er de feltene som sier noe om HVA operatøren så og hvor godt.
 */
export const BildeExifSchema = z.object({
    /** «Apple iPhone 14 Pro», «DJI FC8482», «FLIR Boson». Sier hva slags sensor. */
    kameraModell: z.string().max(120).optional(),
    /** Faktisk brennvidde (mm). */
    brennviddeMm: z.number().positive().max(2000).optional(),
    /**
     * Brennvidde omregnet til 35 mm-ekvivalent. VIKTIGERE enn den faktiske: den
     * kan omsettes direkte til synsfelt, mens rå mm er meningsløs uten
     * sensorstørrelsen — som EXIF sjelden oppgir.
     */
    brennvidde35mmMm: z.number().positive().max(2000).optional(),
    isoVerdi: z.number().int().positive().max(4_000_000).optional(),
    eksponeringstidS: z.number().positive().max(3600).optional(),
    blenderTall: z.number().positive().max(100).optional(),
    /** Firmware/programvare — avslører etterbehandling. */
    programvare: z.string().max(120).optional(),
});
/**
 * Hvilken nord asimuten refererer til.
 *
 * ⚠ DETTE FELTET ER PÅKREVD, OG DET ER MED VILJE.
 * Telefonens magnetometer gir MAGNETISK retning. I Oslofjord-området er
 * deklinasjonen ~+5,2°, som på 1 km flytter målet ~91 m sidelengs. Var feltet
 * valgfritt, ville en app som glemte det sendt magnetisk retning som ble lest som
 * sann — en 5°-feil uten et eneste varsel noe sted. Nå MÅ appen si hvilken nord
 * den mener, og hubben korrigerer `magnetisk` med WMM (`magdec.ts`) før tallet
 * brukes til noe.
 */
export const RetningReferanseSchema = z.enum(['sann', 'magnetisk']);
/** Hvordan asimuten ble bestemt. Avgjør hvor mye vi kan stole på den. */
export const AsimutKildeSchema = z.enum([
    /** EXIF-tag 17 (GPSImgDirection) — kameraets egen retning ved eksponering. */
    'exif-bilderetning',
    /** Rått magnetometer, uten tilt-kompensasjon. Verst: feiler når telefonen vippes. */
    'magnetometer',
    /** Magnetometer rotert til vannrett plan med akselerometeret. Best på telefon. */
    'magnetometer-tiltkompensert',
    /** Gimbal/flykontroller på drone. Sub-grad. */
    'gimbal',
    /** Operatøren pekte selv ut retningen i kartet. */
    'manuell',
]);
/**
 * Sensorens romlige orientering i eksponeringsøyeblikket.
 *
 * PITCH-KONVENSJON: negativ = ned mot bakken, 0 = vannrett, positiv = opp. Samme
 * fortegn som luftfart, MISB ST 0601 og `maalestimat.ts`, så drone-telemetri kan
 * mates rett inn uten konvertering.
 */
export const SensorAttitudeSchema = z.object({
    /** 0–360. Se `retningReferanse` for hvilken nord dette er. */
    asimutDeg: z.number().min(0).max(360),
    retningReferanse: RetningReferanseSchema,
    asimutKilde: AsimutKildeSchema,
    /** Grader, negativ = ned. Utelatt betyr UKJENT, ikke vannrett. */
    pitchDeg: z.number().min(-90).max(90).optional(),
    /** Rulling om siktelinja. Brukes til å rette opp bildet, ikke til geometri. */
    rollDeg: z.number().min(-180).max(180).optional(),
    /** 1σ i grader. Utelatt betyr at sensoren ikke oppgav noe — ikke at den er 0. */
    asimutSigmaDeg: z.number().min(0).max(180).optional(),
    pitchSigmaDeg: z.number().min(0).max(90).optional(),
});
/**
 * Linsas synsfelt (grader). Halve horisontalen er sektorens halvbredde.
 *
 * ⚠ HETER `BildeSynsfelt` FORDI `ptz.ts` ALT HAR EN `Synsfelt` (`{hDeg, vDeg}`).
 * Begge går ut gjennom samme barrel (`@skade/contracts`), så to like navn lar den
 * ene skygge den andre i STILLHET — og de er strukturelt ULIKE, så en konsument
 * ville fått en type som ikke passer uten å forstå hvorfor. Jeg innførte
 * kollisjonen i bølge 194 og fant den først da en NY kollisjon (`dekningshull`)
 * brøt hub-bygget og jeg skannet hele barrel-en (2026-08-17).
 */
export const BildeSynsfeltSchema = z.object({
    horisontalDeg: z.number().positive().max(180),
    vertikalDeg: z.number().positive().max(180).optional(),
});
/**
 * Ett taktisk bilde fra felt.
 *
 * Feltene speiler hva en telefon FAKTISK kan gi oss, ikke et ønskeskjema — samme
 * prinsipp som `GnaPosisjonSchema`. Alt utenom posisjon, tid, opphav og selve
 * bildet er valgfritt: en telefon innendørs har ikke retning, en galleri-import
 * har ikke attitude, og et påkrevd felt der ville tvunget appen til å dikte.
 */
export const GnaBildeInnSchema = z.object({
    lat: z.number().min(-90).max(90),
    lon: z.number().min(-180).max(180),
    /** Eksponeringstidspunkt (ms epoch UTC) — fra bildet, ikke da vi mottok det. */
    tsMs: z.number().int().positive(),
    /** GPS-usikkerhet i meter. Blir CEP på fotografpunktet. */
    noyaktighetM: z.number().min(0).max(100_000).optional(),
    /** Høyde over ellipsoide (HAE) slik GPS-en gir den. Mates til målestimeringen. */
    hoydeM: z.number().min(-1000).max(30_000).optional(),
    opphav: BildeOpphavSchema,
    /** Selve bildet, base64 (uten `data:`-prefiks). */
    bildeBase64: z.string().min(1).max(BILDE_MAKS_BASE64),
    bildeMimeType: z.enum(['image/jpeg', 'image/png', 'image/webp']),
    /** Pikselmål — trengs for å regne fra et punkt i bildet til en vinkel. */
    bildeBreddePx: z.number().int().positive().max(100_000).optional(),
    bildeHoydePx: z.number().int().positive().max(100_000).optional(),
    /** Operatørens egen tekst. Kort med vilje: dette er en observasjon, ikke en rapport. */
    tekst: z.string().max(400).optional(),
    exif: BildeExifSchema.optional(),
    attitude: SensorAttitudeSchema.optional(),
    synsfelt: BildeSynsfeltSchema.optional(),
});
/** Ett bilde per forsendelse. Se konvolutt-merknaden øverst i fila. */
export const GnaBildePakkeSchema = z.object({ bilde: GnaBildeInnSchema });
/** Kildemerke for taktiske bilder som kom over HTTPS-veien. */
export const GNA_BILDE_SOURCE = 'gna-bilde';
/**
 * Nøkkelen i `Track.extra` som bærer bilde-URL-en.
 *
 * ⚠ DEFINERT ETT STED FORDI TO SIDER MÅ VÆRE ENIGE (§3). Hubben skriver den,
 * COP-en leser den for å rendre et faktisk `<img>` i stedet for en tekstrad.
 * Med strengen «Bilde» hardkodet på begge sider ville en omdøping gjort at COP
 * stille falt tilbake til å vise URL-en som tekst — som er nøyaktig feilen den
 * ble innført for å rette (bølge 198).
 */
export const BILDE_EXTRA_NOKKEL = 'Bilde';
/** Ukjent bakkeobjekt (APP-6) — samme verdi `ugs.ts` bruker for det samme. */
const SIDC_UKJENT_BAKKE = 'SUGP-------';
/**
 * Halv sektorbredde (grader) når vi skal tegne hva kameraet så.
 *
 * Rangeringen er bevisst: linsas SYNSFELT er det riktige svaret når vi har det
 * («bildet dekker disse gradene»), peileusikkerheten er det nest beste («vi vet
 * ikke retningen bedre enn dette»), og en default er siste utvei.
 *
 * ⚠ DEFAULTEN ER VID (±20°), IKKE SMAL. En smal default ville TEGNET presisjon
 * vi ikke har: operatøren ser en tynn stråle og tror retningen er godt kjent,
 * når sannheten er at ingen oppgav noe. Vid og stygg er ærlig; smal og pen lyver.
 */
export const SEKTOR_HALVBREDDE_DEFAULT_DEG = 20;
export function sektorHalvbreddeDeg(b) {
    if (b.synsfelt)
        return b.synsfelt.horisontalDeg / 2;
    if (b.attitude?.asimutSigmaDeg !== undefined && b.attitude.asimutSigmaDeg > 0) {
        return b.attitude.asimutSigmaDeg;
    }
    return SEKTOR_HALVBREDDE_DEFAULT_DEG;
}
/**
 * Utled synsfelt (grader) av 35 mm-ekvivalent brennvidde.
 *
 * Standard 36 mm bildebredde: FOV = 2·atan(36/(2·f)). En iPhone-hovedkamera på
 * 26 mm gir ~70°, en 200 mm tele gir ~10°. Vi bruker BARE 35 mm-ekvivalenten:
 * rå brennvidde uten sensorstørrelse kan ikke omsettes til vinkel, og å anta en
 * sensorstørrelse ville vært å gjette på noe som varierer med en faktor 5.
 */
export function synsfeltFraBrennvidde(brennvidde35mmMm) {
    if (!(brennvidde35mmMm > 0))
        return null;
    return (2 * Math.atan(36 / (2 * brennvidde35mmMm)) * 180) / Math.PI;
}
/**
 * Hvor lang sektoren TEGNES når ingen har målt hvor langt sikten rakk.
 *
 * ⚠ DETTE ER EN TEGNELENGDE, IKKE EN MÅLING. Uten en høydemodell å skyte strålen
 * mot vet vi ikke hvor observasjonen slutter — sektoren må likevel ha en ende for
 * å kunne tegnes. Kalleren som bruker denne SKAL merke sporet med at rekkevidden
 * er utegnet (se `bildeInnTilSpor`, som setter «Rekkevidde: ikke målt»), ellers
 * leser operatøren tegnelengden som en avstand noen har fastslått.
 */
export const SIKT_TEGNELENGDE_UTEN_MAAL_M = 2000;
/**
 * Sektoren som beskriver hva kameraet så — eller `null` når retningen er ukjent.
 *
 * `rMaxM` er kalleren sitt ansvar: den vet om målestimeringen ble forsøkt og hvor
 * langt strålen kom. Å gjette en rekkevidde her ville tegnet en sektor som slutter
 * et sted ingen har målt.
 */
export function bildeSektor(b, rMaxM) {
    const a = b.attitude;
    if (!a)
        return null;
    return {
        kind: 'sektor',
        origin: { lat: b.lat, lon: b.lon },
        // ⚠ ALLTID GRADER SANN. Se `asimutSann` — dette er hele grunnen til at
        // `retningReferanse` er et påkrevd felt.
        bearingDeg: asimutSann(b),
        bearingSigmaDeg: sektorHalvbreddeDeg(b),
        rMinM: 0,
        rMaxM,
    };
}
/**
 * Asimuten i GRADER SANN, uansett hva sensoren oppgav.
 *
 * ⚠ DENNE FUNKSJONEN ER GRUNNEN TIL AT `retningReferanse` ER PÅKREVD.
 * Uten den var feltet en ren seremoni: skjemaet TVANG appen til å si «magnetisk»,
 * og så tegnet vi den magnetiske retningen som om den var sann — nøyaktig den
 * 5°-feilen kravet skulle hindre, bare med dokumentasjon på at vi visste bedre.
 * (Jeg innførte presis den feilen 2026-08-17 og fant den ved å oppdage at
 * `magdec.ts` fortsatt hadde null kallsteder. Et påkrevd felt ingen leser er en
 * falsk vakt — samme klasse som `if let self as? Delegat` i AppDelegate.)
 *
 * Deklinasjonen regnes med EKTE WMM (`magdec.ts`, verifisert mot NOAA sin
 * testverdi) i observasjonens punkt og på observasjonens tidspunkt — ikke med en
 * konstant for Oslofjord. Deklinasjonen varierer geografisk og drifter ~7
 * bueminutter i året, og en hardkodet «+5,2°» ville vært riktig ett sted i ett år.
 *
 * Østlig deklinasjon er positiv: sann = magnetisk + deklinasjon.
 */
export function asimutSann(b) {
    const a = b.attitude;
    if (!a)
        return 0;
    if (a.retningReferanse === 'sann')
        return a.asimutDeg;
    const dek = magneticDeclination(b.lat, b.lon, 0, decimalYear(new Date(b.tsMs)));
    return ((a.asimutDeg + dek) % 360 + 360) % 360;
}
/** Menneskelesbar beskrivelse av sensoren, til detaljpanelet i COP. */
function sensorTekst(b) {
    const modell = b.exif?.kameraModell?.trim();
    const opphav = b.opphav === 'live_camera' ? 'kamera' : b.opphav === 'gallery_import' ? 'galleri' : 'ukjent opphav';
    return modell ? `${modell} (${opphav})` : opphav;
}
/**
 * Taktisk bilde → observasjonsspor for FOTOGRAFEN sitt standpunkt.
 *
 * Samme idiom som `geotagInnTilSpor`: et EGET punkt med egen id, ikke personens
 * spor-id. En observasjon skal bli liggende der den ble gjort, også etter at
 * operatøren har gått videre.
 *
 * ⚠ DETTE SPORET ER IKKE MÅLET. Det er der kameraet STO. Målestimatet er et eget
 * spor (`maalestimatTilSpor`), fordi det er en helt annen påstand med helt annen
 * usikkerhet — og fordi det ofte ikke finnes. Å slå dem sammen til ett punkt
 * ville gjort det umulig for operatøren å se om markøren er «her sto han» eller
 * «her tror vi målet er».
 */
export function bildeInnTilSpor(b, opts) {
    const kort = opts.gnaMatrixId.replace(/^@/, '').replace(/:.*$/, '');
    const avsender = (opts.navn ?? '').trim() || kort;
    const tekst = (b.tekst ?? '').trim();
    const spor = {
        id: `bilde:${kort}:${b.tsMs}`,
        kind: 'point',
        affiliation: 'unknown',
        lat: b.lat,
        lon: b.lon,
        sidc: 'SUGP------*****',
        label: tekst ? `FOTO: ${tekst.slice(0, 60)}` : `FOTO · ${avsender}`,
        tsMs: b.tsMs,
        source: GNA_BILDE_SOURCE,
        bevares: true,
        person: { matrixId: opts.gnaMatrixId, navn: opts.navn },
    };
    if (b.noyaktighetM !== undefined)
        spor.cepM = b.noyaktighetM;
    const extra = {};
    extra['Rapportert av'] = avsender;
    extra['Sensor'] = sensorTekst(b);
    if (tekst)
        extra['Tekst'] = tekst;
    if (b.attitude) {
        // Retningen vises i GRADER SANN, fordi det er den kartet tegner. Var kilden
        // magnetisk, vises den RÅ verdien også — operatøren skal kunne se at en
        // korreksjon er gjort, og hvor stor den var. Å bare vise den korrigerte
        // verdien ville skjult at tallet ikke er det telefonen målte.
        const sann = asimutSann(b);
        if (b.attitude.retningReferanse === 'magnetisk') {
            extra['Retning'] =
                `${Math.round(sann)}° sann (${Math.round(b.attitude.asimutDeg)}° magnetisk, WMM-korrigert)`;
        }
        else {
            extra['Retning'] = `${Math.round(sann)}° sann`;
        }
        if (b.attitude.pitchDeg !== undefined) {
            extra['Vinkel'] = `${b.attitude.pitchDeg.toFixed(1)}°`;
        }
        if (b.attitude.asimutSigmaDeg !== undefined) {
            extra['Retningsusikkerhet'] = `±${b.attitude.asimutSigmaDeg.toFixed(1)}°`;
        }
    }
    else {
        // Å utelate feltet ville latt operatøren tro at retningen bare ikke var vist.
        extra['Retning'] = 'ikke oppgitt';
    }
    // Siktsektoren: typet felt på sporet, ikke en streng COP må tolke tilbake.
    const maalt = opts.siktRekkeviddeM;
    const sektor = bildeSektor(b, maalt ?? SIKT_TEGNELENGDE_UTEN_MAAL_M);
    if (sektor) {
        spor.siktsektor = sektor;
        // Uten en målt lengde er sektorens ende en tegnekonvensjon. Det SKAL stå,
        // ellers leses tegnelengden som en fastslått avstand.
        extra['Rekkevidde'] = maalt !== undefined
            ? `${Math.round(maalt)} m (målt mot høydemodell)`
            : 'ikke målt — sektoren er tegnet med fast lengde';
    }
    if (b.synsfelt)
        extra['Synsfelt'] = `${Math.round(b.synsfelt.horisontalDeg)}°`;
    if (b.exif?.brennvidde35mmMm)
        extra['Brennvidde'] = `${Math.round(b.exif.brennvidde35mmMm)} mm (35 mm-ekv.)`;
    // ⚠ Galleri-import merkes SYNLIG i situasjonsbildet, ikke bare i en logg.
    if (b.opphav === 'gallery_import') {
        extra['⚠ Opphav'] = 'Hentet fra galleriet — kan være tatt et annet sted og til en annen tid';
    }
    if (opts.bildeUrl)
        extra[BILDE_EXTRA_NOKKEL] = opts.bildeUrl;
    spor.extra = extra;
    return spor;
}
/**
 * Målestimat → spor for det vi TROR er målet.
 *
 * Returnerer `null` når strålen ikke traff. Det er hele poenget: kalleren skal da
 * tegne sektoren (`bildeSektor`) og IKKE et punkt. En funksjon som alltid ga et
 * spor ville tvunget fram falsk presisjon (§1).
 *
 * Sporet er UKJENT bakkeobjekt med vilje. Vi har utledet et sted, ikke en
 * identitet: hva som står der har ingen sagt noe om.
 */
export function maalestimatTilSpor(b, estimat, opts) {
    if (estimat.kind !== 'treff')
        return null;
    const kort = opts.gnaMatrixId.replace(/^@/, '').replace(/:.*$/, '');
    const u = estimat.usikkerhet;
    const spor = {
        id: `bildemaal:${kort}:${b.tsMs}`,
        kind: 'detection',
        affiliation: 'unknown',
        lat: estimat.lat,
        lon: estimat.lon,
        sidc: SIDC_UKJENT_BAKKE,
        label: `MÅLESTIMAT · ${Math.round(estimat.avstandM)} m`,
        tsMs: b.tsMs,
        source: GNA_BILDE_SOURCE,
        cepM: u.cepM,
        bevares: true,
    };
    spor.extra = {
        // Førstelinja skal si at dette er REGNET UT, ikke observert. En operatør som
        // leser markøren som en observasjon vil overvurdere den.
        'Type': 'Beregnet av bilderetning mot høydemodell — ikke en observasjon',
        'Avstand': `${Math.round(estimat.avstandM)} m fra fotografen`,
        'Terrenghøyde': `${Math.round(estimat.terrengHoydeM)} moh.`,
        'Usikkerhet langs': `±${Math.round(u.storAkseM)} m`,
        'Usikkerhet på tvers': `±${Math.round(u.lilleAkseM)} m`,
        'Fotografpunkt': `bilde:${kort}:${b.tsMs}`,
    };
    return spor;
}
//# sourceMappingURL=gna-bilde.js.map